/**
 * Sidebar Component
 * Navigation menu for available tools
 */
import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  makeStyles,
  tokens,
  Text,
  Spinner,
  Button,
} from "@fluentui/react-components";
import {
  Home24Regular,
  Home24Filled,
  Apps24Regular,
  Apps24Filled,
  Settings24Regular,
  Settings24Filled,
  BrainCircuit24Regular,
  BrainCircuit24Filled,
  ClipboardTask24Regular,
  ClipboardTask24Filled,
  People24Regular,
  People24Filled,
  Globe24Regular,
  Globe24Filled,
  Headset24Regular,
  Headset24Filled,
  TicketDiagonal24Regular,
  TicketDiagonal24Filled,
  DataTrending24Regular,
  DataTrending24Filled,
  CalendarLtr24Regular,
  CalendarLtr24Filled,
  bundleIcon,
  ChatBubblesQuestion24Regular,
} from "@fluentui/react-icons";

import { usePermissions } from "../../auth/usePermissions";
import { AppRoles } from "../../auth/authConfig";
import { useToolRegistry } from "../../tools/useToolRegistry";
import { FeedbackDialog } from "../FeedbackDialog";
import type { Tool } from "../../tools/types";

/**
 * The product release — ONE number for everything a user's experience depends on.
 * Stamped at build by scripts/write-git-env.cjs (never typed by hand): r<N> resolves
 * to exactly one master commit, and every service image carries the same value in
 * its /health. This is what a bug report should quote. See docs/DEPLOYMENT.md.
 */
const RELEASE = (process.env.REACT_APP_RELEASE || "dev").trim();
const BUILD_DATE = (process.env.REACT_APP_BUILD_DATE || "").trim();
const GIT_COMMIT = (process.env.REACT_APP_GIT_COMMIT || "").trim();
const GIT_COMMIT_SHORT = GIT_COMMIT ? GIT_COMMIT.slice(0, 7) : "";
export const APP_VERSION = [`Photon ${RELEASE}`, BUILD_DATE, GIT_COMMIT_SHORT]
  .filter(Boolean)
  .join(" · ");

const HomeIcon = bundleIcon(Home24Filled, Home24Regular);
const AppsIcon = bundleIcon(Apps24Filled, Apps24Regular);
const SettingsIcon = bundleIcon(Settings24Filled, Settings24Regular);
const BrainCircuitIcon = bundleIcon(BrainCircuit24Filled, BrainCircuit24Regular);
const ClipboardTaskIcon = bundleIcon(ClipboardTask24Filled, ClipboardTask24Regular);
const PeopleIcon = bundleIcon(People24Filled, People24Regular);
const GlobeIcon = bundleIcon(Globe24Filled, Globe24Regular);
const HeadsetIcon = bundleIcon(Headset24Filled, Headset24Regular);
const TicketIcon = bundleIcon(TicketDiagonal24Filled, TicketDiagonal24Regular);
const TrendIcon = bundleIcon(DataTrending24Filled, DataTrending24Regular);
const CalendarIcon = bundleIcon(CalendarLtr24Filled, CalendarLtr24Regular);

const toolIconMap: Record<string, React.FC> = {
  "cad-analysis": BrainCircuitIcon,
  "sharepoint-agent": ClipboardTaskIcon,
  "crm": PeopleIcon,
  "client-map": GlobeIcon,
  "support-insights": HeadsetIcon,
  "jira-agent": TicketIcon,
  "executive-dashboard": TrendIcon,
  "conferences": CalendarIcon,
};

const getToolIcon = (toolId: string) => {
  const IconComponent = toolIconMap[toolId];
  if (IconComponent) {
    return <IconComponent />;
  }
  return <AppsIcon />;
};

const useStyles = makeStyles({
  sidebar: {
    width: "260px",
    height: "100%",
    backgroundColor: "#ffffff",
    borderRight: "1px solid #e2e8f0",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    boxShadow: "2px 0 8px rgba(0, 0, 0, 0.03)",
  },
  nav: {
    flex: 1,
    overflow: "auto",
    padding: tokens.spacingVerticalS,
  },
  section: {
    marginBottom: tokens.spacingVerticalL,
  },
  sectionTitle: {
    fontSize: "11px",
    fontWeight: 600,
    color: "#63666A",
    textTransform: "uppercase",
    letterSpacing: "1px",
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
    marginTop: tokens.spacingVerticalS,
    fontFamily: "'Poppins', sans-serif",
  },
  navItem: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    padding: `10px ${tokens.spacingHorizontalM}`,
    borderRadius: "8px",
    cursor: "pointer",
    color: "#2D2926",
    textDecoration: "none",
    transition: "all 0.15s ease",
    margin: `2px ${tokens.spacingHorizontalS}`,
    fontFamily: "'Open Sans', sans-serif",
  },
  navItemActive: {
    backgroundColor: "#004C97",
  },
  navItemText: {
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    padding: tokens.spacingVerticalL,
  },
  footer: {
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
    borderTop: "1px solid #e2e8f0",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: tokens.spacingVerticalS,
  },
  feedbackButton: {
    backgroundColor: "#f0f0f0",
    width: "100%",
  },
  versionText: {
    fontSize: "11px",
    color: "#999",
    textAlign: "center",
  },
});

interface NavItemProps {
  icon: React.ReactNode;
  label: string;
  path: string;
  isActive: boolean;
  onClick: () => void;
}

const NavItem: React.FC<NavItemProps> = ({ icon, label, isActive, onClick }) => {
  const styles = useStyles();

  return (
    <div
      className={`${styles.navItem} ${isActive ? styles.navItemActive : ""}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onClick()}
      style={isActive ? { color: "#ffffff" } : undefined}
    >
      {icon}
      <Text className={styles.navItemText} style={isActive ? { color: "#ffffff" } : undefined}>{label}</Text>
    </div>
  );
};

export const Sidebar: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasRole } = usePermissions();
  const { accessibleTools, isLoading } = useToolRegistry();
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  // Group tools by category
  const toolsByCategory = accessibleTools.reduce<Record<string, Tool[]>>(
    (acc, tool) => {
      const category = tool.category || "General";
      if (!acc[category]) acc[category] = [];
      acc[category].push(tool);
      return acc;
    },
    {}
  );

  return (
    <aside className={styles.sidebar}>
      <nav className={styles.nav}>
        {/* Home */}
        <div className={styles.section}>
          <NavItem
            icon={<HomeIcon />}
            label="Home"
            path="/"
            isActive={location.pathname === "/"}
            onClick={() => navigate("/")}
          />
        </div>

        {/* Tools */}
        {isLoading ? (
          <div className={styles.loading}>
            <Spinner size="small" />
          </div>
        ) : (
          Object.entries(toolsByCategory).map(([category, categoryTools]) => (
            <div key={category} className={styles.section}>
              <Text className={styles.sectionTitle}>{category}</Text>
              {categoryTools.map((tool) => (
                <NavItem
                  key={tool.id}
                  icon={getToolIcon(tool.id)}
                  label={tool.name}
                  path={`/tool/${tool.id}`}
                  isActive={location.pathname === `/tool/${tool.id}`}
                  onClick={() => {
                    if (tool.type === "external" && tool.externalUrl) {
                      window.open(tool.externalUrl, "_blank", "noopener,noreferrer");
                    } else {
                      navigate(`/tool/${tool.id}`);
                    }
                  }}
                />
              ))}
            </div>
          ))
        )}

        {/* Admin */}
        {hasRole(AppRoles.PORTAL_ADMIN) && (
          <div className={styles.section}>
            <Text className={styles.sectionTitle}>Administration</Text>
            <NavItem
              icon={<SettingsIcon />}
              label="Admin Settings"
              path="/admin"
              isActive={location.pathname.startsWith("/admin")}
              onClick={() => navigate("/admin")}
            />
          </div>
        )}
      </nav>
      <div className={styles.footer}>
        <Button
          className={styles.feedbackButton}
          appearance="subtle"
          icon={<ChatBubblesQuestion24Regular />}
          onClick={() => setFeedbackOpen(true)}
        >
          Send Feedback
        </Button>
        <Text
          className={styles.versionText}
          title={GIT_COMMIT ? `git ${GIT_COMMIT} — quote the release (${RELEASE}) in bug reports` : undefined}
        >
          {APP_VERSION}
        </Text>
      </div>
      <FeedbackDialog
        open={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
        version={APP_VERSION}
      />
    </aside>
  );
};

export default Sidebar;
